import React, { useState, useRef, useEffect } from "react";
import { Image } from "@mantine/core";
import {
  Box,
  Grid,
  Typography,
  Dialog,
  IconButton,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { formatAmount } from "./manageRequestsService";
import { getAccessToken } from "../../services/authenticationService";

interface Props {
  data: Record<string, any>;
  onDataChange?: (updatedData: Record<string, any>) => void;
  status: string | undefined
}

const CollectionRequest: React.FC<Props> = ({ data, onDataChange, status }) => {
  const [openImage, setOpenImage] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [localData, setLocalData] = useState<Record<string, any>>(data || {});
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const mode = data.modeOfPayment;

  // --- click outside → disable edit
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setEditMode(false);
      }
    };
    if (editMode) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editMode]);

  // --- handle input change
  const handleChange = (value: string) => {
  if (value && !/^\d*\.?\d*$/.test(value)) return;
  if (localData.cash === "0" && value !== "0") {
    value = value.replace(/^0+/, ""); 
  }

  if (value.trim() === "") value = "0";

  const updated = { ...localData, cash: value };
  setLocalData(updated);
  onDataChange?.(updated);
};

  // --- details list
  const getDetailsList = () => {
    switch (mode.toLowerCase()) {
      case "cash":
        return [{ label: "Amount", value: localData?.cash }];
      case "cheque":
        return [
          { label: "Cheque Number", value: data?.chequeNumber },
          { label: "Amount", value: data?.amount },
          { label: "Bank Name", value: data?.bankName },
        ];
      case "upi":
      case "bank_transfer":
        return [
          { label: "IBAN", value: data?.iban },
          { label: "Account No.", value: data?.accountNo },
          { label: "Transaction ID", value: data?.transactionId },
        ];
      case "pos":
        return [
          { label: "Amount", value: data?.amount },
          { label: "Transaction ID", value: data?.transactionId },
        ];
      case "e_wallet":
        return [
          { label: "Wallet Number", value: data?.walletNumber },
          { label: "Provider", value: data?.provider },
          { label: "Amount", value: data?.amount },
          { label: "Transaction ID", value: data?.transactionId },
        ];
      default:
        return [];
    }
  };

  const detailsList = getDetailsList();

  const loadImageWithAuth = async (url: string, token: string) => {
    try {
      const response = await fetch(`${url}?access_token=${token}`);

      if (!response.ok) throw new Error("Unable to load image");

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  useEffect(() => {
    const token = getAccessToken();

    loadImageWithAuth(data?.paymentProofImage, token ?? "").then((blobUrl) => {
      setImgUrl(blobUrl);
    });
  }, [data?.paymentProofImage]);


  // --- styles
  const uniformBoxStyle = {
    borderRadius: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minWidth: "160px",
    px: 1.5,
    py: 0.5,
    fontSize: "12px",
    fontWeight: 500,
    border: "0.5px solid #bfbfc0",
  };

  const boxStyle = {
    px: 2,
    bgcolor: "white",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    pb: 2,
    fontSize: "12px",
    position: "relative" as const,
  };

  return (
    <Box sx={boxStyle} ref={containerRef}>
      {/* ✏️ Edit Icon visible only for CASH mode */}
      {/* {mode === "Cash" && (status?.toLowerCase() !== "approved" && status?.toLowerCase() !== "rejected" && status?.toLowerCase() !== "rfc_approved" && status?.toLowerCase() !== "rfc_rejected") && (
        <IconButton
          onClick={() => setEditMode(!editMode)}
          sx={{
            position: "absolute",
            top: 5,
            right: 5,
            width: 26,
            height: 26,
            color: editMode ? "#2e7d32" : "#00897b",
            bgcolor: "rgba(0, 137, 123, 0.08)",
            borderRadius: "50%",
            transition: "all 0.2s ease",
            "& svg": { fontSize: 14 },
            "&:hover": {
              bgcolor: "rgba(0, 137, 123, 0.15)",
              transform: "scale(1.05)",
            },
            "&:active": {
              bgcolor: "rgba(0, 137, 123, 0.25)",
              transform: "scale(0.95)",
            },
          }}
        >
          {editMode ? <CheckIcon /> : <EditIcon />}
        </IconButton>
      )} */}

      <Grid container spacing={0.8} direction="column">
        <Grid item>
          <Typography
            variant="subtitle2"
            color="#bfbfc0"
            sx={{ fontSize: "10px" }}
          >
            Collection Detail
          </Typography>
        </Grid>
        <Grid item>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box key={"mode"} sx={{ ...uniformBoxStyle, backgroundColor: "#E9F2FF", border: "none" }}>
                {"Mode :  "}{mode.toUpperCase()}
              </Box>
          </Box>
        </Grid>

        <Grid item>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {detailsList.map((item, index) => (
              <Box key={index} sx={{ ...uniformBoxStyle }}>
                {item.label}:
                {editMode && mode === "Cash" ? (
                  <input
                    type="text"
                    value={localData.cash || ""}
                    onChange={(e) => handleChange(e.target.value)}
                    style={{
                      border: "none",
                      background: "transparent",
                      outline: "none",
                      width: "80px",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#333",
                      marginLeft: 4,
                    }}
                  />
                ) : mode === "Cash" ? (
                  <Typography sx={{ fontSize: 12, ml: 0.5 }}>
                    {formatAmount(localData.cash) || "0"}
                  </Typography>
                ) : (
                   <Typography sx={{ fontSize: 12, ml: 0.5 }}>
                    {item.label === "Amount" ? formatAmount(item.value || "0") : (item.value || "-")}
                  </Typography>
                )}
              </Box>
            ))}

            {/* View Image */}
            {data?.paymentProofImage && (
              <Box
                sx={{
                  ...uniformBoxStyle,
                  justifyContent: "space-between",
                  gap: 1,
                  cursor: "pointer",
                  transition: "background-color 0.2s ease",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
                onClick={() => setOpenImage(true)}
              >
                <ImageIcon sx={{ fontSize: 16, color: "#6e6e6e" }} />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    textDecoration: "underline",
                  }}
                >
                  View Image
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={openImage}
        onClose={() => setOpenImage(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <img
            src = {imgUrl ?? ""}
            alt="Payment Proof"
            style={{ maxWidth: "100%", borderRadius: 8 }}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default CollectionRequest;


